import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Upload,
  Trash2,
  Edit,
  Plus,
  ArrowLeft,
  AlertCircle,
  FileText,
  Save,
  Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { managementService, Kit, Document } from '@/services/management.service';

const API_URL = import.meta.env.VITE_API_URL;

interface KitImage {
  fileUploaded: string | null;
  base64: string | null;
  fileType: string | null;
}

interface ExtendedKit extends Kit {
  imagesItem?: KitImage[];
}

interface KitForm extends Partial<ExtendedKit> {
  imagesItem?: Array<KitImage>;
}

const PreCadastrosSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Estados - Documento
  const [document, setDocument] = useState<Document | null>(null);
  const [fileSign, setFileSign] = useState<{
    fileUploaded: string | null;
    base64: string | null;
    fileType: string | null;
  } | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  // Estados - Kits
  const [kits, setKits] = useState<ExtendedKit[]>([]);
  const [loadingKits, setLoadingKits] = useState(false);
  const [editingKit, setEditingKit] = useState<ExtendedKit | null>(null);
  const [kitDialogOpen, setKitDialogOpen] = useState(false);
  const [savingKit, setSavingKit] = useState(false);
  const [kitForm, setKitForm] = useState<KitForm>({
    name: '',
    description: '',
    price: 0,
    imagesItem: [
      { fileUploaded: null, base64: null, fileType: null },
      { fileUploaded: null, base64: null, fileType: null },
      { fileUploaded: null, base64: null, fileType: null },
    ],
  });
  const [deletingKitId, setDeletingKitId] = useState<number | null>(null);

  // Estados - Pontuação
  const [scoreCriteria, setScoreCriteria] = useState<any[]>([]);
  const [loadingScore, setLoadingScore] = useState(false);
  const [editedScores, setEditedScores] = useState<Record<number, number>>({});
  const [savingScoreId, setSavingScoreId] = useState<number | null>(null);

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUser(userData);

      if (userData.role !== 'admin') {
        toast.error('Acesso restrito a administradores');
        navigate('/pre-cadastros');
        return;
      }

      loadAllData();
    } else {
      navigate('/authentication/side-login');
    }
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadDocument(), loadKits(), loadScoreCriteria()]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadDocument = async () => {
    try {
      const result = await managementService.getDocuments();
      if (result) {
        setDocument(result);
        setFileSign({
          fileUploaded: result.path || null,
          base64: null,
          fileType: null,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar documento:', error);
    }
  };

  const loadKits = async () => {
    setLoadingKits(true);
    try {
      const result = await managementService.getKits();
      const transformedKits = (result || []).map((kit: any) => ({
        ...kit,
        imagesItem: [
          { fileUploaded: kit.imageOnePath || null, base64: null, fileType: null },
          { fileUploaded: kit.imageTwoPath || null, base64: null, fileType: null },
          { fileUploaded: kit.imageThreePath || null, base64: null, fileType: null },
        ],
      }));
      setKits(transformedKits);
    } catch (error) {
      console.error('Erro ao carregar kits:', error);
      toast.error('Erro ao carregar kits');
    } finally {
      setLoadingKits(false);
    }
  };

  const loadScoreCriteria = async () => {
    setLoadingScore(true);
    try {
      const result = await managementService.getScoreCriteria();
      if (result?.data) {
        setScoreCriteria(result.data);
      }
    } catch (error) {
      console.error('Erro ao carregar critérios de pontuação:', error);
    } finally {
      setLoadingScore(false);
    }
  };

  const handleScoreChange = (id: number, value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setEditedScores(prev => ({ ...prev, [id]: numValue }));
    }
  };

  const handleSaveScore = async (id: number) => {
    const newPontos = editedScores[id];
    if (newPontos === undefined) return;

    setSavingScoreId(id);
    try {
      await managementService.updateScoreCriterio(id, newPontos);
      toast.success('Pontuação atualizada com sucesso');
      setEditedScores(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      await loadScoreCriteria();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar pontuação');
    } finally {
      setSavingScoreId(null);
    }
  };

  const handleDocumentFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (file.type !== 'application/pdf') {
      toast.error('Por favor, selecione um arquivo PDF');
      return;
    }

    // Validar tamanho (10MB máximo)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('O arquivo não pode exceder 10MB');
      return;
    }

    // Converter para base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setFileSign({
        fileUploaded: file.name,
        base64: base64,
        fileType: file.type,
      });
    };
    reader.onerror = () => {
      toast.error('Erro ao carregar o arquivo');
    };
    reader.readAsDataURL(file);
  };

  const handleUploadDocument = async () => {
    if (!fileSign?.base64 || !document?.id) {
      toast.error('Selecione um arquivo para fazer upload');
      return;
    }

    setUploadingDocument(true);
    try {
      await managementService.updateContractDocSign(document.id, {
        fileUploaded: fileSign.fileUploaded,
        base64: fileSign.base64,
        fileType: fileSign.fileType,
      });

      toast.success('Documento atualizado com sucesso');
      setFileSign(null);
      loadDocument();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao atualizar documento');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleKitImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem');
      return;
    }

    // Validar tamanho (5MB máximo)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem não pode exceder 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      const newImages = [...(kitForm.imagesItem || [])];
      newImages[index] = { fileUploaded: null, base64, fileType: file.type };
      setKitForm({ ...kitForm, imagesItem: newImages });
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...(kitForm.imagesItem || [])];
    newImages[index] = { fileUploaded: null, base64: null, fileType: null };
    setKitForm({ ...kitForm, imagesItem: newImages });
  };

  const openNewKitDialog = () => {
    setEditingKit(null);
    setKitForm({
      name: '',
      description: '',
      price: 0,
      imagesItem: [
        { fileUploaded: null, base64: null, fileType: null },
        { fileUploaded: null, base64: null, fileType: null },
        { fileUploaded: null, base64: null, fileType: null },
      ],
    });
    setKitDialogOpen(true);
  };

  const openEditKitDialog = (kit: ExtendedKit) => {
    setEditingKit(kit);
    setKitForm({
      ...kit,
      imagesItem: kit.imagesItem || [
        { fileUploaded: null, base64: null, fileType: null },
        { fileUploaded: null, base64: null, fileType: null },
        { fileUploaded: null, base64: null, fileType: null },
      ],
    });
    setKitDialogOpen(true);
  };

  const handleSaveKit = async () => {
    if (!kitForm.name?.trim() || !kitForm.description?.trim() || !kitForm.price) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (kitForm.price < 0) {
      toast.error('O preço não pode ser negativo');
      return;
    }

    setSavingKit(true);
    try {
      const updateData: any = {
        nome: kitForm.name,
        descricao: kitForm.description,
        preco: kitForm.price,
        images: {},
      };

      // Preparar imagens no formato correto
      const images = kitForm.imagesItem || [];
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        updateData.images[i.toString()] = {
          fileUploaded: img.base64 ? null : (img.fileUploaded || null),
          base64: img.base64 || null,
          fileType: img.fileType || null,
        };
      }

      if (editingKit) {
        await managementService.updateKit(editingKit.id, updateData);
        toast.success('Kit atualizado com sucesso');
      } else {
        // Para novo kit, usar o serviço apropriado
        // Este é um exemplo, ajuste conforme a API
        toast.success('Kit criado com sucesso (função não implementada no serviço)');
      }

      setKitDialogOpen(false);
      loadKits();
    } catch (error: any) {
      console.error('Erro ao salvar kit:', error);
      toast.error(error.message || 'Erro ao salvar kit');
    } finally {
      setSavingKit(false);
    }
  };

  const handleDeleteKit = async (kitId: number) => {
    if (confirm('Tem certeza que deseja deletar este kit?')) {
      setDeletingKitId(kitId);
      try {
        // A função delete não foi implementada no serviço
        toast.info('Função de deleção não implementada');
      } catch (error: any) {
        toast.error(error.message || 'Erro ao deletar kit');
      } finally {
        setDeletingKitId(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/pre-cadastros')}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie documentos e kits de pré-cadastros
          </p>
        </div>
      </div>

      {/* Documento de Assinatura */}
      <Card>
        <CardHeader>
          <CardTitle>Documento de Assinatura</CardTitle>
          <CardDescription>
            Gerencie o documento que deve ser assinado por novas revendedoras
          </CardDescription>
        </CardHeader>
        <CardContent>
          {document ? (
            <div className="space-y-4">
              {/* Arquivo Atual */}
              <div className="p-4 bg-muted rounded-lg border">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-sm">Arquivo Atual</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Atualizado em{' '}
                      {new Date(document.updatedAt).toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                    {fileSign?.fileUploaded && (
                      <a
                        href={`${API_URL}/pre-register/documents/open/${fileSign.fileUploaded}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary text-sm hover:underline mt-2 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        {fileSign.fileUploaded}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Novo Arquivo */}
              <div className="space-y-3">
                <Label>Novo Arquivo</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="application/pdf"
                    onChange={handleDocumentFileSelect}
                    disabled={uploadingDocument}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleUploadDocument}
                    disabled={!fileSign?.base64 || uploadingDocument}
                  >
                    {uploadingDocument && (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    )}
                    {uploadingDocument ? 'Enviando...' : 'Enviar'}
                  </Button>
                </div>               
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Erro ao carregar documento</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kits */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Kits</CardTitle>
              <CardDescription>
                Gerencie os kits oferecidos nas pré-cadastros
              </CardDescription>
            </div>
            {/* <Button onClick={openNewKitDialog} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Novo Kit
            </Button> */}
          </div>
        </CardHeader>
        <CardContent>
          {loadingKits ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : kits.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Nenhum kit cadastrado</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kits.map((kit) => (
                <div
                  key={kit.id}
                  className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Imagem Principal */}
                  <div className="bg-muted h-40 flex items-center justify-center overflow-hidden">
                    {kit.imagesItem?.[0]?.fileUploaded ? (
                      <img
                        src={`${API_URL}/pre-register/kit/item-image/${kit.imagesItem[0].fileUploaded}`}
                        alt={kit.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <AlertCircle className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>

                  {/* Conteúdo */}
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-semibold text-sm line-clamp-2">{kit.name}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {kit.description}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <div>
                        <p className="text-xs text-muted-foreground">Preço</p>
                        <p className="font-semibold text-sm">
                          R$ {Number(kit.price).toFixed(2)}
                        </p>
                      </div>
                      {/* <Badge variant="secondary">{kit.id}</Badge> */}
                    </div>

                    {/* Imagens Extras */}
                    <div className="flex gap-1">
                      {kit.imagesItem?.map(
                        (img, idx) =>
                          img?.fileUploaded && (
                            <img
                              key={idx}
                              src={`${API_URL}/pre-register/kit/item-image/${img.fileUploaded}`}
                              alt={`Kit ${idx}`}
                              className="w-12 h-12 rounded object-cover"
                            />
                          )
                      )}
                    </div>

                    {/* Ações */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => openEditKitDialog(kit)}
                        size="sm"
                        className="flex-1"
                        variant="outline"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </Button>
                      {/* <Button
                        onClick={() => handleDeleteKit(kit.id)}
                        size="sm"
                        variant="destructive"
                        disabled={deletingKitId === kit.id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button> */}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pontuação / Score */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Critérios de Pontuação
              </CardTitle>
              <CardDescription>
                Gerencie os pontos atribuídos a cada resposta do formulário de pré-cadastro
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingScore ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : scoreCriteria.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Nenhum critério cadastrado</p>
            </div>
          ) : (
            <div className="space-y-6">
              {(() => {
                const groups = new Map<string, any[]>();
                scoreCriteria.forEach((c) => {
                  if (!groups.has(c.criterio)) groups.set(c.criterio, []);
                  groups.get(c.criterio)!.push(c);
                });

                return Array.from(groups.entries()).map(([criterioName, items]) => (
                  <div key={criterioName} className="border rounded-lg overflow-hidden">
                    <div className="bg-muted px-4 py-2 border-b">
                      <p className="font-semibold text-sm capitalize">
                        {criterioName.replace(/_/g, ' ')}
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          ({items[0]?.pergunta})
                        </span>
                      </p>
                    </div>
                    <div className="divide-y">
                      {items.map((item: any) => {
                        const currentPontos = editedScores[item.id] ?? item.pontos;
                        const isEdited = editedScores[item.id] !== undefined && editedScores[item.id] !== item.pontos;

                        return (
                          <div key={item.id} className="flex items-center justify-between px-4 py-3 gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{item.resposta_label}</p>
                              {/* <p className="text-xs text-muted-foreground">
                                Tipo: {item.tipo} &middot; Campo: {item.campo}
                              </p> */}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Input
                                type="number"
                                value={currentPontos}
                                onChange={(e) => handleScoreChange(item.id, e.target.value)}
                                className="w-20 text-center"
                              />
                              <Button
                                size="sm"
                                variant={isEdited ? 'default' : 'ghost'}
                                disabled={!isEdited || savingScoreId === item.id}
                                onClick={() => handleSaveScore(item.id)}
                              >
                                {savingScoreId === item.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Save className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Edição/Criação de Kit */}
      <Dialog open={kitDialogOpen} onOpenChange={setKitDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingKit ? 'Editar Kit' : 'Novo Kit'}
            </DialogTitle>
            <DialogDescription>
              {editingKit
                ? 'Atualize as informações do kit'
                : 'Preencha as informações do novo kit'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Nome */}
            <div>
              <Label htmlFor="kit-name">Nome</Label>
              <Input
                id="kit-name"
                placeholder="Nome do kit"
                value={kitForm.name || ''}
                onChange={(e) =>
                  setKitForm({ ...kitForm, name: e.target.value })
                }
                disabled={savingKit}
              />
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="kit-description">Descrição</Label>
              <Textarea
                id="kit-description"
                placeholder="Descrição do kit"
                value={kitForm.description || ''}
                onChange={(e) =>
                  setKitForm({ ...kitForm, description: e.target.value })
                }
                disabled={savingKit}
                className="min-h-[80px]"
              />
            </div>

            {/* Preço */}
            <div>
              <Label htmlFor="kit-price">Preço (R$)</Label>
              <Input
                id="kit-price"
                type="number"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={kitForm.price || ''}
                onChange={(e) =>
                  setKitForm({ ...kitForm, price: parseFloat(e.target.value) || 0 })
                }
                disabled={savingKit}
              />
            </div>

            {/* Imagens */}
            <div>
              <Label>Imagens</Label>
              <div className="grid grid-cols-3 gap-4">
                {(kitForm.imagesItem || []).map((img, index) => (
                  <div
                    key={index}
                    className="border-2 border-dashed rounded-lg p-4 text-center"
                  >
                    {img.base64 || img.fileUploaded ? (
                      <>
                        <img
                          src={img.base64 || `${API_URL}/pre-register/kit/item-image/${img.fileUploaded}` || ''}
                          alt={`Kit ${index + 1}`}
                          className="w-full h-24 object-cover rounded mb-2"
                        />
                        <Button
                          onClick={() => handleRemoveImage(index)}
                          size="sm"
                          variant="destructive"
                          disabled={savingKit}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <label className="cursor-pointer flex flex-col items-center gap-2">
                        <Upload className="w-6 h-6 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Imagem {index + 1}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleKitImageSelect(e, index)}
                          disabled={savingKit}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-2 pt-6 border-t">
              <Button
                onClick={handleSaveKit}
                disabled={savingKit}
                className="flex-1"
              >
                {savingKit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {savingKit ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                onClick={() => setKitDialogOpen(false)}
                variant="outline"
                disabled={savingKit}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreCadastrosSettings;
